import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Avatar, Chip,
  CircularProgress, Alert, Grid, Divider, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Button,
} from '@mui/material';
import {
  ArrowBack, LocalFireDepartment, FitnessCenter, Timer,
  TrendingUp, Whatshot, CalendarToday, Scale, Restaurant,
  Egg, Grain, WaterDrop, Straighten,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { analyticsAPI, usersAPI } from '../services/api';

// ── Thin bar chart with gradients ─────────────────────────────────────────
function MiniBarChart({ data, valueKey, labelKey, color = 'primary.main', height = 100 }) {
  if (!data || data.length === 0) return (
    <Box display="flex" alignItems="center" justifyContent="center" height={height}>
      <Typography variant="caption" color="text.secondary">No data available</Typography>
    </Box>
  );
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const barWidth = Math.min(28, Math.max(8, Math.floor(260 / data.length) - 6));

  return (
    <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={0}
        sx={{ height: height + 24, minWidth: data.length * (barWidth + 8), px: 0.5 }}
      >
        {data.map((d, i) => {
          const pct = Math.max(((d[valueKey] || 0) / max) * height, 3);
          return (
            <Tooltip key={i} title={`${d[labelKey]}: ${Math.round(d[valueKey] || 0)}`} arrow>
              <Box
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  width: barWidth + 8, cursor: 'default',
                  '&:hover .bar': { opacity: 1, transform: 'scaleY(1.04)', transformOrigin: 'bottom' },
                }}
              >
                <Box
                  className="bar"
                  sx={{
                    width: barWidth,
                    height: `${pct}px`,
                    borderRadius: '4px 4px 2px 2px',
                    background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
                    opacity: 0.82,
                    transition: 'all 0.2s ease',
                    boxShadow: `0 2px 8px ${color}40`,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontSize: 9, color: 'text.disabled', mt: 0.5, whiteSpace: 'nowrap', userSelect: 'none' }}
                >
                  {d[labelKey]}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = 'primary.main' }) {
  return (
    <Card variant="outlined" sx={{
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      '&:hover': { boxShadow: `0 4px 20px ${color}22`, borderColor: color + '55' },
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2,
          background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
        <Typography variant="h6" fontWeight={700} lineHeight={1.2} sx={{ fontSize: '1.1rem' }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
        {sub && <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Format helpers ─────────────────────────────────────────────────────────
const fmtDuration = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtDateFull = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function UserAnalytics() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [mealData, setMealData] = useState(null);
  const [mealDays, setMealDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [analyticsRes, userRes, mealRes] = await Promise.all([
        analyticsAPI.getUserStats(userId),
        usersAPI.getOne(userId),
        analyticsAPI.getUserMealLogs(userId, mealDays),
      ]);
      setData(analyticsRes.data.data);
      setUserProfile(userRes.data.data);
      setMealData(mealRes.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [userId, mealDays]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box p={3}><Alert severity="error">{error}</Alert></Box>
  );

  if (!data) return null;

  const {
    user, totalSessions, totalCalories, totalSets, totalDurationSeconds,
    recentSessions, weightLogs, muscleGroups, weeklyData,
  } = data;

  // Prepare chart data
  const sessionChartData = recentSessions.slice(-14).map(s => ({
    label: fmtDate(s.date),
    calories: Math.round(s.caloriesBurned),
    sets: s.completedSets,
    duration: Math.round(s.durationSeconds / 60),
  }));

  const weightChartData = weightLogs.slice(-14).map(w => ({
    label: fmtDate(w.date),
    weight: w.weightKg,
  }));

  const weeklyChartData = weeklyData.map((w, i) => ({
    label: `W${i + 1}`,
    sessions: w.sessions,
    calories: Math.round(w.calories),
  }));

  const totalMuscleSessions = muscleGroups.reduce((s, m) => s + m.count, 0);

  return (
    <Box p={3}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{ height: 3, background: 'linear-gradient(90deg, #7C3AED, #A78BFA)' }} />
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ flexShrink: 0 }}>
              <ArrowBack fontSize="small" />
            </IconButton>

            <Avatar sx={{
              bgcolor: 'primary.main',
              width: 48, height: 48,
              fontSize: 20, fontWeight: 700,
              flexShrink: 0,
              boxShadow: '0 0 0 3px rgba(124,58,237,0.15)',
            }}>
              {user.name?.charAt(0)?.toUpperCase()}
            </Avatar>

            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight={700} noWrap>{user.name}</Typography>
                {userProfile?.role && (
                  <Chip label={userProfile.role} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {userProfile?.membershipStatus && (
                  <Chip
                    label={userProfile.membershipStatus}
                    size="small"
                    color={userProfile.membershipStatus === 'active' ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {user.email}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Straighten sx={{ fontSize: '14px !important' }} />}
              onClick={() => navigate(`/dashboard/users/${userId}/measurements`)}
              sx={{ flexShrink: 0 }}
            >
              Measurements
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Top stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<Whatshot />} label="Current Streak" value={`${user.currentStreak} days`} color="#FF6B35" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<FitnessCenter />} label="Total Workouts" value={totalSessions} color="primary.main" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<LocalFireDepartment />} label="Total Calories" value={`${Math.round(totalCalories).toLocaleString()} kcal`} color="#FF4757" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<TrendingUp />} label="Total Sets" value={totalSets.toLocaleString()} color="#2ED573" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<Timer />} label="Total Time" value={fmtDuration(totalDurationSeconds)} color="#1E90FF" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<CalendarToday />}
            label="Last Workout"
            value={user.lastWorkoutDate ? fmtDate(user.lastWorkoutDate) : '—'}
            color="#A29BFE"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        {/* Calories per session chart */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            <Box sx={{ height: 3, background: 'linear-gradient(90deg, #FF4757, #FF6B81)' }} />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF4757' }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Calories Burned — Last 14 Sessions
                </Typography>
              </Stack>
              <MiniBarChart data={sessionChartData} valueKey="calories" labelKey="label" color="#FF4757" height={100} />
            </CardContent>
          </Card>
        </Grid>

        {/* Sets per session chart */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            <Box sx={{ height: 3, background: 'linear-gradient(90deg, primary.main, #A29BFE)' }} />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Sets Completed — Last 14 Sessions
                </Typography>
              </Stack>
              <MiniBarChart data={sessionChartData} valueKey="sets" labelKey="label" color="primary.main" height={100} />
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly sessions */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            <Box sx={{ height: 3, background: 'linear-gradient(90deg, #2ED573, #7BED9F)' }} />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2ED573' }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Weekly Sessions — Last 8 Weeks
                </Typography>
              </Stack>
              <MiniBarChart data={weeklyChartData} valueKey="sessions" labelKey="label" color="#2ED573" height={100} />
            </CardContent>
          </Card>
        </Grid>

        {/* Weight trend */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
            <Box sx={{ height: 3, background: 'linear-gradient(90deg, #1E90FF, #74B9FF)' }} />
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Scale sx={{ fontSize: 16, color: '#1E90FF' }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">Weight Trend (kg)</Typography>
              </Stack>
              {weightChartData.length > 0 ? (
                <>
                  <MiniBarChart data={weightChartData} valueKey="weight" labelKey="label" color="#1E90FF" height={100} />
                  <Stack direction="row" justifyContent="space-between" mt={1.5}
                    sx={{ px: 0.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Start: <b>{weightChartData[0]?.weight} kg</b>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Latest: <b>{weightChartData[weightChartData.length - 1]?.weight} kg</b>
                    </Typography>
                    {weightChartData.length > 1 && (() => {
                      const diff = (weightChartData[weightChartData.length - 1]?.weight - weightChartData[0]?.weight).toFixed(1);
                      return (
                        <Typography variant="caption" fontWeight={700}
                          color={diff < 0 ? 'success.main' : diff > 0 ? 'error.main' : 'text.secondary'}>
                          {diff > 0 ? '+' : ''}{diff} kg
                        </Typography>
                      );
                    })()}
                  </Stack>
                </>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height={100}>
                  <Typography variant="caption" color="text.secondary">No weight data synced yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Muscle groups */}
      {muscleGroups.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2.5, mb: 2, overflow: 'hidden' }}>
          <Box sx={{ height: 3, background: 'linear-gradient(90deg, #A29BFE, primary.main)' }} />
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>Muscle Groups Trained</Typography>
            <Stack spacing={1.5}>
              {muscleGroups.map((m) => (
                <Box key={m._id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{m._id}</Typography>
                    <Chip
                      label={`${m.count} sessions`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'primary.main18', color: 'primary.main', fontWeight: 600 }}
                    />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(m.count / totalMuscleSessions) * 100}
                    sx={{
                      height: 7, borderRadius: 4,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, primary.main, #A29BFE)',
                        borderRadius: 4,
                      }
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Recent sessions table */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{ height: 3, background: 'linear-gradient(90deg, #FF6B35, #FFA07A)' }} />
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Recent Workout Sessions</Typography>
          {recentSessions.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <Typography variant="body2" color="text.secondary">No sessions synced yet</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Workout</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Duration</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Sets</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Calories</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Muscles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...recentSessions].reverse().map((s, i) => (
                    <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDateFull(s.date)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{s.dayName || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtDuration(s.durationSeconds)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{s.completedSets}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                        <Chip
                          label={`${Math.round(s.caloriesBurned)} kcal`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FF475718', color: '#FF4757', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(s.musclesTargeted || []).slice(0, 3).map((m, j) => (
                            <Chip key={j} label={m} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'primary.main18', color: 'primary.main', fontWeight: 500 }} />
                          ))}
                          {(s.musclesTargeted || []).length > 3 && (
                            <Chip label={`+${s.musclesTargeted.length - 3}`} size="small"
                              sx={{ fontSize: '0.6rem', height: 18 }} />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Meal Nutrition Section ─────────────────────────────────────── */}
      <Box mt={2} mb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Restaurant sx={{ color: '#FF6B35', fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>Nutrition Tracking</Typography>
          </Stack>
          <ToggleButtonGroup
            value={mealDays}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) setMealDays(v); }}
          >
            {[7, 14, 30].map(d => (
              <ToggleButton key={d} value={d} sx={{ px: 1.5, fontSize: '0.75rem' }}>
                {d}d
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        {!mealData ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
        ) : mealData.summary.totalLogs === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                <Typography variant="body2" color="text.secondary">No meal logs synced yet</Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Meal summary stats */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<Restaurant />} label="Meals Logged" value={mealData.summary.totalLogs} color="#FF6B35" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<LocalFireDepartment />} label="Total Calories" value={`${mealData.summary.totalCalories.toLocaleString()} kcal`} color="#FF4757" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<LocalFireDepartment />} label="Avg Daily Calories" value={`${mealData.summary.avgDailyCalories} kcal`} color="#FFA07A" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<CalendarToday />} label="Days Tracked" value={`${mealData.summary.daysTracked} days`} color="#A29BFE" />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              {/* Daily calorie chart */}
              <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                  <Box sx={{ height: 3, background: 'linear-gradient(90deg, #FF6B35, #FFA07A)' }} />
                  <CardContent sx={{ pt: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF6B35' }} />
                      <Typography variant="subtitle2" fontWeight={700}>Daily Calories — Last {mealDays} Days</Typography>
                    </Stack>
                    <MiniBarChart
                      data={mealData.dailyTotals.map(d => ({ label: d._id.slice(5), calories: Math.round(d.calories) }))}
                      valueKey="calories" labelKey="label" color="#FF6B35" height={100}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Meal type breakdown */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', height: '100%' }}>
                  <Box sx={{ height: 3, background: 'linear-gradient(90deg, #A29BFE, primary.main)' }} />
                  <CardContent sx={{ pt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={2}>Meal Type Frequency</Typography>
                    <Stack spacing={1.5}>
                      {mealData.mealTypeBreakdown.map((m) => {
                        const label = m._id.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                        const maxCount = Math.max(...mealData.mealTypeBreakdown.map(x => x.count), 1);
                        return (
                          <Box key={m._id}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                              <Typography variant="caption" fontWeight={500}>{label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {m.count}× · {Math.round(m.avgCalories)} kcal avg
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={(m.count / maxCount) * 100}
                              sx={{
                                height: 6, borderRadius: 3,
                                bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                  background: 'linear-gradient(90deg, primary.main, #A29BFE)',
                                  borderRadius: 3,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Daily macro breakdown chart */}
            <Grid container spacing={2} mb={2}>
              {[
                { key: 'protein', label: 'Protein (g)', color: 'primary.main', icon: <Egg sx={{ fontSize: 14 }} /> },
                { key: 'carbs',   label: 'Carbs (g)',   color: '#26C6DA', icon: <Grain sx={{ fontSize: 14 }} /> },
                { key: 'fat',     label: 'Fat (g)',     color: '#4CAF82', icon: <WaterDrop sx={{ fontSize: 14 }} /> },
              ].map(({ key, label, color, icon }) => (
                <Grid item xs={12} md={4} key={key}>
                  <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
                    <CardContent sx={{ pt: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
                        <Box sx={{ color }}>{icon}</Box>
                        <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                      </Stack>
                      <MiniBarChart
                        data={mealData.dailyTotals.map(d => ({ label: d._id.slice(5), value: Math.round(d[key]) }))}
                        valueKey="value" labelKey="label" color={color} height={70}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Recent meal logs table */}
            <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ height: 3, background: 'linear-gradient(90deg, #FF6B35, #FFA07A)' }} />
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                  Recent Meal Logs ({mealData.recentLogs.length})
                </Typography>
                <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider', maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Meal</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Calories</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Protein</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Carbs</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Fat</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Items</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mealData.recentLogs.map((log, i) => (
                        <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDateFull(log.date)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{log.mealName}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.mealType.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FF6B3518', color: '#FF6B35', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {Math.round(log.totalCalories)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'primary.main' }}>
                            {log.totalProteinG?.toFixed(1)}g
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#26C6DA' }}>
                            {log.totalCarbsG?.toFixed(1)}g
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#4CAF82' }}>
                            {log.totalFatG?.toFixed(1)}g
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {(log.items || []).slice(0, 2).map((item, j) => (
                                <Chip key={j} label={`${item.name} ${item.weightG}g`} size="small"
                                  sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.hover' }} />
                              ))}
                              {(log.items || []).length > 2 && (
                                <Chip label={`+${log.items.length - 2}`} size="small"
                                  sx={{ fontSize: '0.6rem', height: 18 }} />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      {/* Profile info */}
      {userProfile && (
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>Profile</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[
                ['Age', userProfile.age ? `${userProfile.age} yrs` : '—'],
                ['Gender', userProfile.gender || '—'],
                ['Height', userProfile.height ? `${userProfile.height} cm` : '—'],
                ['Weight', userProfile.weight ? `${userProfile.weight} kg` : '—'],
                ['Goal', userProfile.fitnessGoal?.replace(/_/g, ' ') || '—'],
                ['Activity', userProfile.activityLevel?.replace(/_/g, ' ') || '—'],
                ['Gym', userProfile.gymId?.name || '—'],
                ['Trainer', userProfile.trainerId?.name || '—'],
                ['Member Since', userProfile.createdAt ? fmtDateFull(userProfile.createdAt) : '—'],
                ['Membership', userProfile.membershipStatus || '—'],
              ].map(([label, value]) => (
                <Grid item xs={6} sm={4} md={3} key={label}>
                  <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>{value}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
